import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StepNav, { FLUID_STEPS } from "@/components/StepNav";
import { useInfusionPump } from "@/hooks/useInfusionPump";
import { FurtherReading } from "@/components/FurtherReading";
import { PumpDevice } from "@/components/fluid/PumpDevice";
import { FluidBalanceChart } from "@/components/fluid/FluidBalanceChart";
import { OrderCard, ReferenceRatesCard } from "@/components/fluid/OrderCard";
import { SaveSession } from "@/components/SaveSession";
import { Button } from "@/components/ui/button";
import { computeFluidPlan, buildOrderText } from "@/data/fluids";
import { Droplets, ChevronLeft } from "lucide-react";
import { toast } from "sonner";

export default function FluidPump() {
  const navigate = useNavigate();

  // Scenario patient lives on this page now (no separate intake screen).
  const [species, setSpecies] = useState("dog");
  const [weight, setWeight] = useState("20");

  const patientLike = useMemo(
    () => ({ species, weightKg: weight }),
    [species, weight]
  );
  const plan = useMemo(() => computeFluidPlan(patientLike), [patientLike]);
  const [mode, setMode] = useState("surgery");
  const [fluid, setFluid] = useState("LRS");
  const order = plan.orders[mode];
  const orderText = useMemo(() => buildOrderText(plan, mode), [plan, mode]);

  // pump programming (rate, vtbi & time are interdependent: vtbi = rate × time)
  const [rate, setRate] = useState("0");
  const [vtbi, setVtbi] = useState("0");
  const [timeMin, setTimeMin] = useState("0");
  const [field, setField] = useState("rate"); // rate | vtbi | time
  const [showCalc, setShowCalc] = useState(false);
  const [score, setScore] = useState(null);

  // pump run state + infusion loop (extracted hook)
  const rateNum = parseFloat(rate) || 0;
  const vtbiNum = parseFloat(vtbi) || 0;
  const timeNum = parseFloat(timeMin) || 0; // minutes

  const {
    running,
    setRunning,
    infused,
    setInfused,
    elapsedMin,
    setElapsedMin,
    status,
    setStatus,
    alarm,
    setAlarm,
    speed,
    setSpeed,
  } = useInfusionPump(rateNum, vtbiNum);

  /* ---------------- programming helpers ---------------- */
  const editable = !running && status !== "INFUSING";

  // Set one field and re-derive the linked field so vtbi = rate × time always holds.
  // Rate is the anchor: editing rate/vtbi re-derives time; editing time re-derives vtbi.
  const applyField = (f, str) => {
    const val = !str || str === "0" ? 0 : parseFloat(str) || 0;
    setScore(null);
    if (f === "rate") {
      setRate(str);
      if (val > 0) setTimeMin(String(Math.round((vtbiNum / val) * 60)));
    } else if (f === "vtbi") {
      setVtbi(str);
      if (rateNum > 0) setTimeMin(String(Math.round((val / rateNum) * 60)));
    } else {
      setTimeMin(str);
      if (rateNum > 0) setVtbi(String(Math.round(rateNum * (val / 60))));
    }
  };

  const pressDigit = (d) => {
    if (!editable) return toast.error("Stop the pump before editing settings");
    const cur = field === "rate" ? rate : field === "vtbi" ? vtbi : timeMin;
    const base = cur === "0" ? "" : cur;
    const nextStr = (base + d).slice(0, 5);
    applyField(field, nextStr === "" ? "0" : nextStr);
  };

  // DELETE = backspace one digit in the current field (corrects a mis-entry).
  const delDigit = () => {
    if (!editable) return toast.error("Stop the pump before editing settings");
    const cur = field === "rate" ? rate : field === "vtbi" ? vtbi : timeMin;
    const next = cur.length > 1 ? cur.slice(0, -1) : "0";
    applyField(field, next);
  };

  const bump = (delta) => {
    if (!editable) return;
    const cur = field === "rate" ? rateNum : field === "vtbi" ? vtbiNum : timeNum;
    applyField(field, String(Math.max(0, Math.round((cur + delta) * 10) / 10)));
  };

  // Instant competency check against the 2024 AAHA target for this order.
  const verify = () => {
    if (rateNum <= 0 || vtbiNum <= 0)
      return toast.error("Set a rate and volume first");
    const rateErr = Math.abs(rateNum - order.rate) / order.rate;
    const vtbiErr = Math.abs(vtbiNum - order.vtbi) / order.vtbi;
    const ratePts = Math.max(0, 50 * (1 - rateErr / 0.1));
    const vtbiPts = Math.max(0, 50 * (1 - vtbiErr / 0.1));
    const total = Math.round(ratePts + vtbiPts);
    const grade =
      total >= 90 ? "A" : total >= 80 ? "B" : total >= 70 ? "C" : total >= 60 ? "D" : "F";
    setScore({
      total,
      grade,
      rateOk: rateErr <= 0.02,
      vtbiOk: vtbiErr <= 0.05,
      orderRate: order.rate,
      orderVtbi: order.vtbi,
    });
  };

  const start = () => {
    if (status === "COMPLETE") return;
    if (rateNum <= 0) return toast.error("Set an infusion rate first");
    if (vtbiNum <= 0) return toast.error("Set a volume to be infused (VTBI)");
    // validate against order
    const rateOk = Math.abs(rateNum - order.rate) <= Math.max(1, order.rate * 0.03);
    const vtbiOk = Math.abs(vtbiNum - order.vtbi) <= Math.max(5, order.vtbi * 0.05);
    if (rateOk && vtbiOk) {
      toast.success(`Settings match ${orderText.heading.toLowerCase()} ✓`);
    } else {
      const msgs = [];
      if (!rateOk) msgs.push(`rate should be ${order.rate} mL/h`);
      if (!vtbiOk) msgs.push(`VTBI should be ${order.vtbi} mL`);
      toast.warning(`Check the order — ${msgs.join(" · ")}`);
    }
    setStatus("INFUSING");
    setRunning(true);
    setAlarm(false);
  };

  const stop = () => {
    setRunning(false);
    if (status !== "COMPLETE") setStatus("STOPPED");
  };

  // Reset only the infusion run state (used when the scenario/order changes).
  const resetRun = () => {
    setRunning(false);
    setInfused(0);
    setElapsedMin(0);
    setStatus("STOPPED");
    setAlarm(false);
    setScore(null);
  };

  // CLEAR / RESET = wipe ALL programmed settings and the run state.
  const clearAll = () => {
    resetRun();
    setRate("0");
    setVtbi("0");
    setTimeMin("0");
    setField("rate");
  };

  /* ---------------- derived display ---------------- */
  const pct = vtbiNum > 0 ? Math.min(100, (infused / vtbiNum) * 100) : 0;
  const bagLevel = 100 - pct;
  const remainingMin = rateNum > 0 ? ((vtbiNum - infused) / rateNum) * 60 : 0;
  const dripDur = rateNum > 0 ? Math.max(0.12, Math.min(1.4, 240 / rateNum)) : 1.4;

  const statusColor =
    status === "INFUSING"
      ? "text-[#39FF14]"
      : status === "COMPLETE"
      ? "text-[#FF3333]"
      : "text-slate-400";

  return (
    <div className="min-h-screen bg-[#F4F7F9]">
      <StepNav
        current={1}
        steps={FLUID_STEPS}
        title="Fluid pump"
        subtitle="Volumetric infusion pump training"
      />
      <main className="max-w-6xl mx-auto px-6 py-8 fade-up">
        <header className="mb-6">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-[#0047AB] font-semibold mb-2">
            <Droplets className="w-3.5 h-3.5" />
            <span>Fluid delivery</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-slate-900">
            Set up the infusion pump
          </h1>
          <p className="text-base text-slate-600 mt-2 max-w-2xl">
            Read the order, calculate the correct rate from the patient&apos;s weight,
            and program the pump.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
          {/* ---------------- Left: clinical order ---------------- */}
          <div className="space-y-5">
            <OrderCard
              species={species}
              weight={weight}
              plan={plan}
              mode={mode}
              fluid={fluid}
              order={order}
              orderText={orderText}
              showCalc={showCalc}
              setSpecies={setSpecies}
              setWeight={setWeight}
              setMode={setMode}
              setFluid={setFluid}
              setShowCalc={setShowCalc}
              resetRun={resetRun}
            />

            <ReferenceRatesCard plan={plan} />

            <Button
              variant="outline"
              onClick={() => navigate("/sections")}
              data-testid="back-to-home-btn"
              className="text-slate-600"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back to Simulator Hub
            </Button>
          </div>

          {/* ---------------- Right: the pump ---------------- */}
          <PumpDevice
            bagLevel={bagLevel}
            fluid={fluid}
            running={running}
            dripDur={dripDur}
            alarm={alarm}
            statusColor={statusColor}
            status={status}
            field={field}
            rateNum={rateNum}
            vtbiNum={vtbiNum}
            timeNum={timeNum}
            infused={infused}
            pct={pct}
            elapsedMin={elapsedMin}
            remainingMin={remainingMin}
            score={score}
            speed={speed}
            setField={setField}
            pressDigit={pressDigit}
            delDigit={delDigit}
            bump={bump}
            start={start}
            stop={stop}
            setAlarm={setAlarm}
            verify={verify}
            clearAll={clearAll}
            setSpeed={setSpeed}
          />
        </div>

        <FluidBalanceChart infused={infused} species={species} weight={parseFloat(weight) || 0} />

        {/* further reading — always available on this page */}
        {score && (
          <SaveSession
            simulatorId="fluidpump"
            simulatorName="Fluid Delivery Pump"
            score={score.total}
            grade={score.grade}
            summary={`Grade ${score.grade} · ${score.total}/100 vs AAHA target`}
          />
        )}
        <FurtherReading topic="fluids" className="mt-6" />
      </main>
    </div>
  );
}
